import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import MeasureWrapper from './MeasureWrapper';
import { defaultKeyExtractor } from './helpers';
import useVirtualization from './useVirtualization';

/**
 * FlatList
 *
 * A virtualised list for flat (non-sectioned) data.
 *
 * Rendering modes:
 *   virtualized  — only items within [visibleRange] are in the DOM, with top/bottom
 *                  spacer divs maintaining scroll height (default when items.length
 *                  >= virtualizationThreshold).
 *   flat         — all items rendered without spacers (small lists).
 *
 * Ref API (via forwardRef):
 *   scrollToKey(key, options?)           → Promise<{ success, key, index }>
 *   scrollToIndex(index, options?)       → Promise<{ success, key, index }>
 *   jumpToBottom(options?)
 *   scrollToBottomInstant()
 *   getScrollContainer()                 → HTMLElement | null
 *   takeScrollSnapshot(direction)
 *   clearScrollSnapshot()
 */
const FlatListBase = (props, ref) => {
   const {
      items = [],
      renderItem,
      containerRef,
      itemHeight = 80,
      overscan,
      getItemHeight,
      keyExtractor = defaultKeyExtractor,
      onScroll,
      inverted = false,
      initialScrollKey = null,
      initialScrollIndex = null,
      initialScrollBlock = 'center',
      isHeaderItem,
      ItemSeparatorComponent = null,
      ListEmptyComponent = null,
      enableVirtualization = true,
      virtualizationThreshold = 50,
   } = props;

   // ─── Rendering Mode ────────────────────────────────────────────────────────

   const renderingMode = useMemo(
      () => (enableVirtualization && items.length >= virtualizationThreshold ? 'virtualized' : 'flat'),
      [enableVirtualization, items.length, virtualizationThreshold],
   );

   // ─── Core Hook ─────────────────────────────────────────────────────────────

   const {
      visibleRange,
      isReady,
      offsetTop,
      offsetBottom,
      measureItem,
      scrollToKey,
      scrollToIndex,
      jumpToBottom,
      takeScrollSnapshot,
      clearScrollSnapshot,
   } = useVirtualization({
      items,
      containerRef,
      itemHeight,
      overscan,
      getItemHeight,
      keyExtractor,
      onScroll,
      inverted,
      initialScrollKey,
      initialScrollIndex,
      initialScrollBlock,
      isHeaderItem,
   });

   // ─── Imperative Handle ─────────────────────────────────────────────────────

   useImperativeHandle(
      ref,
      () => ({
         scrollToKey,
         scrollToIndex,
         jumpToBottom,
         scrollToBottomInstant: () => jumpToBottom({ smooth: false }),
         getScrollContainer: () => containerRef?.current,
         takeScrollSnapshot,
         clearScrollSnapshot,
      }),
      [scrollToKey, scrollToIndex, jumpToBottom, containerRef, takeScrollSnapshot, clearScrollSnapshot],
   );

   // ─── Renderers ─────────────────────────────────────────────────────────────

   // Non-virtualised: renders every item in the DOM (used for small lists)
   const renderFlatItems = useCallback(
      () =>
         items.map((item, index) => {
            const key = keyExtractor(item, index);
            return (
               <React.Fragment key={key}>
                  <div id={key} data-key={key} data-index={index} className="virtualized-item">
                     {renderItem({ item, index, isReady })}
                  </div>
                  {ItemSeparatorComponent && index !== items.length - 1 && <>{ItemSeparatorComponent}</>}
               </React.Fragment>
            );
         }),
      [items, keyExtractor, renderItem, ItemSeparatorComponent, isReady],
   );

   // Virtualised: only items in [visibleRange] + spacers for the rest
   const renderVirtualizedItems = useCallback(() => {
      const { start, end } = visibleRange;
      return items.slice(start, end).map((item, offset) => {
         const index = start + offset;
         const key = keyExtractor(item, index);
         return (
            <MeasureWrapper key={key} index={index} measureItem={measureItem}>
               <div id={key} data-key={key} data-index={index} className="virtualized-item">
                  {renderItem({ item, index, measureItem, isReady })}
               </div>
            </MeasureWrapper>
         );
      });
   }, [items, visibleRange, renderItem, measureItem, keyExtractor, isReady]);

   // ─── Render ────────────────────────────────────────────────────────────────

   if (items.length === 0) return ListEmptyComponent ? <>{ListEmptyComponent}</> : null;

   if (renderingMode === 'flat') return <>{renderFlatItems()}</>;

   return (
      <div
         style={{
            opacity: isReady ? 1 : 0,
            transition: 'opacity 0.15s ease-in',
            position: 'relative',
            ...(inverted ? { display: 'flex', flexDirection: 'column-reverse' } : {}),
         }}
      >
         {offsetTop > 0 && <div className="virtualized-spacer" style={{ height: offsetTop, flexShrink: 0 }} />}
         {renderVirtualizedItems()}
         {offsetBottom > 0 && <div className="virtualized-spacer" style={{ height: offsetBottom, flexShrink: 0 }} />}
      </div>
   );
};

const FlatList = forwardRef(FlatListBase);

FlatListBase.propTypes = {
   items: PropTypes.array,
   renderItem: PropTypes.func.isRequired,
   containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
   itemHeight: PropTypes.number,
   overscan: PropTypes.number,
   getItemHeight: PropTypes.func,
   keyExtractor: PropTypes.func,
   onScroll: PropTypes.func,
   inverted: PropTypes.bool,
   initialScrollKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   initialScrollIndex: PropTypes.number,
   initialScrollBlock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   isHeaderItem: PropTypes.func,
   ItemSeparatorComponent: PropTypes.node,
   ListEmptyComponent: PropTypes.node,
   enableVirtualization: PropTypes.bool,
   virtualizationThreshold: PropTypes.number,
};

export default FlatList;
